import { Box, Flex, Text, Divider, useBreakpointValue } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface ExpertiseBoxProps {
    title: string;
    text: string;
    color: string;
    icon: JSX.Element;
    index: number;
    isMiddle?: boolean;
}
const MotionFlex = motion(Flex);

const ExpertiseBox = (props: ExpertiseBoxProps) => {
    const { title, text, color, icon, index, isMiddle } = props;
    const [hovered, setHovered] = useState(false);

    const isMobile = useBreakpointValue({ base: false, md: true });

    const controls = useAnimation();
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    const desktopVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: index * 0.2,
                type: "spring",
                stiffness: 100,
            },
        },
    };

    const mobileVariants = {
        hidden: { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                delay: index * 0.2,
                type: "spring",
                stiffness: 100,
            },
        },
    };

    return (
        <MotionFlex
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={isMobile ? mobileVariants : desktopVariants}
            flexDirection={"column"}
            minH={"320px"}
            maxW={"350px"}
            borderWidth={2}
            borderColor={"white.300"}
            marginLeft={isMiddle ? { base: "0", md: "-0.5" } : "0"}
            marginRight={isMiddle ? { base: "0", md: "-0.5" } : "0"}
            alignItems={"center"}
        >
            {/* Header */}
            <Flex
                flexDirection={"row"}
                padding={"5%"}
                textAlign={"center"}
                color={"white.200"}
                alignItems={"center"}
                backgroundColor={color}
                margin={3}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {/* Icon */}
                <motion.div
                    animate={{ rotate: hovered ? [0, 30, -30, 30, -30, 0] : 0 }}
                >
                    <Box fontSize="5xl" zIndex={1}>
                        {icon}
                    </Box>
                </motion.div>

                {/* Title */}
                <Text
                    fontSize={"2xl"}
                    fontFamily={"Source Code Pro Variable"}
                    fontWeight={"600"}
                >
                    {title}
                </Text>
            </Flex>

            {/* Details */}
            <Flex flexDirection={"column"} paddingBottom={"5%"}>
                {/* H3 tag */}
                <Flex
                    flexDirection={"row"}
                    minH={"20px"}
                    width={"100%"}
                    paddingLeft={"5%"}
                    paddingRight={"5%"}
                    alignItems={"center"}
                >
                    <Text
                        fontSize={"sm"}
                        fontFamily={"Source Code Pro Variable"}
                        color={"white.400"}
                        fontWeight={"600"}
                    >
                        {"<h3>"}
                    </Text>
                </Flex>

                {/* Text and line */}
                <Flex
                    flexDirection={"row"}
                    height={"120px"}
                    width={"100%"}
                    paddingLeft={"10%"}
                    paddingRight={"5%"}
                >
                    {/* Divider */}
                    <Divider
                        orientation={"vertical"}
                        borderColor={"white.400"}
                        borderWidth={1}
                        height={"100%"}
                    />

                    {/* Text */}
                    <Box
                        display={"flex"}
                        alignItems={"center"}
                        padding={"5%"}
                        height={"100%"}
                        fontSize={"md"}
                        textAlign={"left"}
                        fontFamily={"Source Code Pro Variable"}
                        color={"white.200"}
                    >
                        {text}
                    </Box>
                </Flex>

                {/* H3 tag */}
                <Flex
                    flexDirection={"row"}
                    minH={"20px"}
                    width={"100%"}
                    paddingLeft={"5%"}
                    paddingRight={"5%"}
                    alignItems={"center"}
                >
                    <Text
                        fontSize={"sm"}
                        fontFamily={"Source Code Pro Variable"}
                        color={"white.400"}
                        fontWeight={"600"}
                    >
                        {"</h3>"}
                    </Text>
                </Flex>
            </Flex>
        </MotionFlex>
    );
};

export default ExpertiseBox;
